import {useState, useCallback, useContext, useEffect} from 'react'
import PropTypes from 'prop-types';
import {
  Paper,
  TextField,
  Button,
} from '@material-ui/core';

import PhoneIcon from 'UI/Screens/Main/Sections/Contact/Blocks/ContactForm/Icons/PhoneIcon';
import MailIcon from 'UI/Screens/Main/Sections/Contact/Blocks/ContactForm/Icons/MailIcon';
import useLocalized from 'CustomHooks/useLocalized';
import ValidationService from 'Services/ValidationService';
import Recaptcha from 'UI/Components/Recaptcha/Recaptcha';
import {
  PHONE_NUMBER,
  EMAIL_ADDRESS,
  DIRECTOR_NAME,
  DIRECTOR_PHONE_NUMBER,
  OPERATIONS_MANAGER_NAME,
  OPERATIONS_MANAGER_PHONE_NUMBER,
  OPENING_HOURS,
} from 'Constants/Options';

import { NotificationContext } from 'App';
import WebService from 'Services/WebService';
import ErrorMessageService from 'Services/ErrorMessageService';
import {useGetHomeContent} from "../../../../../../../Data/ApiHooks/UseContentHook";

const initialFormState = {
  name: '',
  phoneNumber: '',
  email: '',
  notes: '',
};

const ContactForm = ({
  className,
}) => {
  const [contactData, setContactData] = useState(initialFormState);


  const { data } = useGetHomeContent();

  const [businessHours, setBusinessHours] = useState(null);

  const handleChange = useCallback((e) => {
    setContactData({
      ...contactData,
      [e.target.name]: e.target.value,
    });
  }, [contactData]);

  const [errors, setErrors] = useState({
    name: false,
    phoneNumber: false,
    email: false,
    notes: false,
    captha: true,
  });

  const handleCapthaSuccess = useCallback(() => {
    setErrors({
      ...errors,
      captha: false,
    });
  }, [errors]);

  const validateForm = useCallback(() => {
    setErrors({
      ...errors,
      name: !contactData.name,
      phoneNumber: !contactData.phoneNumber,
      email: !contactData.email || !ValidationService.isValidEmail(contactData.email),
      notes: !contactData.notes,
    });
  }, [contactData.email, contactData.name, contactData.notes, contactData.phoneNumber, errors]);

  const isValidForm = useCallback(() => {
    validateForm();
    return !ValidationService.hasErrors(errors);
  }, [errors, validateForm]);


  const successMessage = useLocalized('contact_form_sent_success');
  const addNotification = useContext(NotificationContext);
  const [isSending, setIsSending] = useState(false);
  const handleFormSubmit = useCallback(() => {
    if (!isValidForm()) {
      return;
    }
    const sendRequestFormOptions = {
      method: 'POST',
      path: 'contact',
      data: contactData,
    };

    setIsSending(true);
    WebService.getSharedInstance().callWebservice(sendRequestFormOptions).then(() => {
      addNotification(successMessage, 'success');
      setIsSending(false);
      setContactData(initialFormState);
    }).catch((e) => {
      addNotification(ErrorMessageService.getErrorMessage(e), 'error');
      setIsSending(false);
    });
  }, [addNotification, contactData, isValidForm, successMessage]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setBusinessHours(
      data.business_hours
    )
  }, [data, setBusinessHours]);

  return (
    <Paper className={`contact-form ${className}`}>
      <div className="contact-form__left">
        <div className="contact-form__title">
          { data?.contact_title }
        </div>
        <div className="contact-form__info">
          <div className="contact-form__info--icon">
            <PhoneIcon />
          </div>
          <a href={`tel: ${ data?.contact_phone }`} className="contact-form__info--text">
            { data?.contact_phone }
          </a>
        </div>
        <div className="contact-form__info">
          <div className="contact-form__info--icon">
            <MailIcon />
          </div>
          <div className="contact-form__info--text">
            { data?.contact_email }
          </div>
        </div>
        <div className="contact-form__info">
          <div className="contact-form__info--text">
            <b>
              { data?.operator_1_name }
            </b>
            {` - ${useLocalized('director')}`}
            <br />
            <a href={`tel: ${ data?.operator_1_phone }`} className="contact-form__info--text">
              { data?.operator_1_phone }
            </a>
          </div>
        </div>
        <div className="contact-form__info">
          <div className="contact-form__info--text">
            <b>
              { data?.operator_2_name }
            </b>
            {` - ${useLocalized('operations_manager')}`}
            <br />
            <a href={`tel: ${ data?.operator_2_phone }`} className="contact-form__info--text">
              { data?.operator_2_phone }
            </a>
          </div>
        </div>
        <div className="contact-form__line" />
        <div className="contact-form__title">
          {useLocalized('business_hours')}
        </div>
        <div className="contact-form__info-streched">
          <div className="contact-form__info--text">
            {useLocalized('weekdays')}
          </div>
          <div className="contact-form__info--text" dangerouslySetInnerHTML={{ __html: `${businessHours && businessHours["mon-fri"][0]}<sup>AM</sup> - ${businessHours && businessHours["mon-fri"][1]}<sup>PM</sup>` }} />
        </div>
        <div className="contact-form__info-streched">
          <div className="contact-form__info--text">
            {useLocalized('saturday')}
          </div>
          <div className="contact-form__info--text" dangerouslySetInnerHTML={{ __html: `${businessHours && businessHours["sat"][0]}<sup>AM</sup> - ${businessHours && businessHours["sat"][1]}<sup>PM</sup>` }} />
        </div>
        <div className="contact-form__info-streched">
          <div className="contact-form__info--text">
            {useLocalized('sunday')}
          </div>
          <div className="contact-form__info--text" dangerouslySetInnerHTML={{ __html: businessHours && !businessHours["sun"][0] ? 'Closed' : `${businessHours && businessHours["sun"][0]}<sup>AM</sup> - ${businessHours && businessHours["sun"][1]}<sup>PM</sup>` }} />
        </div>
      </div>
      <div className="contact-form__right">
        <div className="contact-form__title">
          {useLocalized('get_in_touch')}
        </div>
        <div className="contact-form__form-container">
          <TextField
            id="contact-form-name"
            label={useLocalized('form_name')}
            variant="filled"
            name="name"
            value={contactData.name}
            error={errors.name}
            onChange={handleChange}
            type="text"
            fullWidth
            required
          />
          <TextField
            id="contact-form-phone-number"
            label={useLocalized('form_phone_number')}
            variant="filled"
            name="phoneNumber"
            value={contactData.phoneNumber}
            error={errors.phoneNumber}
            onChange={handleChange}
            type="tel"
            fullWidth
            required
          />
          <TextField
            id="contact-form-email"
            label={useLocalized('form_email')}
            variant="filled"
            name="email"
            value={contactData.email}
            error={errors.email}
            onChange={handleChange}
            type="email"
            fullWidth
            required
          />
          <TextField
            id="contact-form-email"
            label={useLocalized('form_notes')}
            variant="filled"
            name="notes"
            value={contactData.notes}
            error={errors.notes}
            onChange={handleChange}
            type="text"
            multiline
            fullWidth
            required
          />
          <Recaptcha onSuccess={handleCapthaSuccess} />
          <Button
            onClick={handleFormSubmit}
            color="primary"
            variant="contained"
            fullWidth={false}
            size="large"
            disabled={isSending}
          >
            {useLocalized('send')}
          </Button>
        </div>
      </div>
    </Paper>
  );
};

ContactForm.propTypes = {
  className: PropTypes.string,
};

export default ContactForm;
